import { useMemo } from "react"

import { HandleAddToCollectionDialog } from "~/components/collections/dialogs/HandleAddToCollectionDialog"
import { HideVariantsDialog } from "~/components/npi/HideVariantsDialog"
import { LifecycleConfirmationDialog } from "~/components/npi/LifecycleConfirmationDialog"
import { ReactivateLifecycleDialog } from "~/components/npi/ReactivateLifecycleDialog"
import Commandbar from "~/components/planning/Commandbar"
import { EditSkuTagsDialog } from "~/components/planning/Tags/EditSkuTagsDialog"
import MultiEditDialog from "~/components/planning/MultiEditDialog/MultiEditDialog"
import ReactivateSkusDialog, {
  type ReactivateVariantData,
} from "~/components/npi/ReactivateSkusDialog"
import { getActionDisabledTooltip, variantNeedsActivation } from "~/constants/npi"
import type { NpiTabContext } from "~/constants/productTable"
import {
  useSelectedVariantHandlers,
  useSelectedVariantState,
} from "~/context/SelectedVariantsProvider"
import { useVariantsByIds } from "~/hooks/useVariants"
import type { INpiVariantSummary } from "~/server/api/routers/npi"
import { reviewActivatedFromOf } from "~/utils/reviewActivatedFrom"
import { getCommonVendors } from "~/utils/vendorUtils"
import type { IVariantSummary } from "~/hooks/useVariantTableData"

type Props = {
  selectedVariantIds: string[]
  selectedVariantCount: number
  clearSelectedVariantIds: () => void
  tabContext: NpiTabContext
}

export function ProductManagementCommandBar({
  selectedVariantIds,
  selectedVariantCount,
  clearSelectedVariantIds,
  tabContext,
}: Props) {
  const { getSelectedVariants } = useSelectedVariantHandlers<INpiVariantSummary | IVariantSummary>()
  const { selectedVariantCount: stateCount } = useSelectedVariantState()

  const selectedVariants = useMemo(() => getSelectedVariants(), [getSelectedVariants, stateCount])

  // For Edit settings dialog — try to get variants from sku store (works for Active tab)
  const storeVariants = useVariantsByIds(selectedVariantIds)
  // Fallback: NPI variant data has skuIds directly
  const fallbackVariants = useMemo(
    () =>
      selectedVariants.map((v) => ({
        variantId: v.variantId,
        skuIds: "skuIds" in v ? v.skuIds : [],
      })),
    [selectedVariants],
  )
  const skuIds = useMemo(() => {
    if (storeVariants.length > 0) return storeVariants.flatMap((v) => v.skuIds)
    return fallbackVariants.flatMap((v) => v.skuIds)
  }, [storeVariants, fallbackVariants])

  const commonVendors = useMemo(() => {
    if (storeVariants.length > 0) return getCommonVendors(storeVariants)
    return []
  }, [storeVariants])

  // EditSkuTags resolves skuIds from the SKU store itself, which only holds Active-tab
  // (shouldBeShown) variants. Supply an override only for the tabs the store can't
  // resolve — inbox/hidden/phased-out — from NPI variant data; undefined = use the store.
  const tagVariants = storeVariants.length > 0 ? undefined : fallbackVariants

  // NPI variant data for AddToCollection dialog
  const npiVariantData = useMemo(
    () =>
      selectedVariants
        .filter((v): v is INpiVariantSummary => "settings" in v && "skuIds" in v)
        .map((v) => ({
          variantId: v.variantId,
          variantName: v.variantName,
          skuIds: v.skuIds,
          hasSalesBasedForecast: v.settings.hasSalesBasedForecast,
          activatedFrom: v.settings.activatedFrom,
          isNpi: v.isNpi,
        })),
    [selectedVariants],
  )

  const { hasMultipleProductTypes, commonProductType } = useMemo(() => {
    const types = new Set(
      selectedVariants.map((v) => {
        const settings = v.settings as { productType?: string | null } | undefined
        return settings?.productType
      }),
    )
    const common = types.size === 1 ? [...types][0] : undefined
    const productType = common === "noos" || common === "fashion" ? common : undefined
    return {
      hasMultipleProductTypes: types.size > 1,
      commonProductType: productType as "noos" | "fashion" | undefined,
    }
  }, [selectedVariants])

  const { hiddenCount, phasedOutCount } = useMemo(() => {
    let hidden = 0
    let phasedOut = 0
    for (const v of selectedVariants) {
      const status = (v as { inactiveStatus?: string }).inactiveStatus
      if (status === "hidden") hidden++
      if (status === "phasedOut") phasedOut++
    }
    return { hiddenCount: hidden, phasedOutCount: phasedOut }
  }, [selectedVariants])

  const hideMode =
    hiddenCount === 0
      ? ("hide" as const)
      : hiddenCount === selectedVariants.length
        ? ("unhide" as const)
        : ("mixed" as const)

  const reactivateVariantData = useMemo<ReactivateVariantData[]>(
    () =>
      selectedVariants
        .filter((v): v is INpiVariantSummary => "settings" in v && "isNpi" in v)
        .map((v) => ({
          isNpi: v.isNpi,
          activatedFrom: v.settings?.activatedFrom,
        })),
    [selectedVariants],
  )

  const hasActivatedFrom = reactivateVariantData.some((v) => v.activatedFrom != null)

  const activationDateDisabled =
    !!getActionDisabledTooltip("activationDate", tabContext) && !hasActivatedFrom

  return (
    <>
      {selectedVariantCount > 0 && (
        <Commandbar count={selectedVariantCount} onClear={clearSelectedVariantIds}>
          <MultiEditDialog
            selectedSkuIds={skuIds}
            selectedVariantCount={selectedVariantCount}
            resetSelectedProductIds={clearSelectedVariantIds}
            commonVendors={commonVendors}
          />
          <HandleAddToCollectionDialog
            variantIds={selectedVariantIds}
            npiVariantData={npiVariantData}
            needsActivation={selectedVariants.some((v) =>
              variantNeedsActivation(v.settings, reviewActivatedFromOf(v)),
            )}
            productType={commonProductType}
            disabled={
              !!getActionDisabledTooltip("addToCollection", tabContext) || hasMultipleProductTypes
            }
            disabledTooltip={
              getActionDisabledTooltip("addToCollection", tabContext) ??
              (hasMultipleProductTypes
                ? "Cannot add to collection: selected variants have different product types"
                : undefined)
            }
          />
          <HideVariantsDialog
            variantIds={selectedVariantIds}
            mode={hideMode}
            hiddenCount={hiddenCount}
            disabled={!!getActionDisabledTooltip("hide", tabContext)}
            disabledTooltip={getActionDisabledTooltip("hide", tabContext)}
          />
          <EditSkuTagsDialog variantIds={selectedVariantIds} variantsOverride={tagVariants} />
          <ReactivateSkusDialog
            selectedVariantIds={selectedVariantIds}
            variantData={reactivateVariantData}
            disabled={activationDateDisabled}
            disabledTooltip={
              activationDateDisabled
                ? getActionDisabledTooltip("activationDate", tabContext)
                : undefined
            }
          />
          <LifecycleConfirmationDialog
            variantIds={selectedVariantIds}
            field="phasingOutFrom"
            disabled={!!getActionDisabledTooltip("phasingOut", tabContext)}
            disabledTooltip={getActionDisabledTooltip("phasingOut", tabContext)}
          />
          {phasedOutCount > 0 && phasedOutCount === selectedVariants.length ? (
            <ReactivateLifecycleDialog
              variantIds={selectedVariantIds}
              disabled={!!getActionDisabledTooltip("reactivate", tabContext)}
              disabledTooltip={getActionDisabledTooltip("reactivate", tabContext)}
            />
          ) : (
            <LifecycleConfirmationDialog
              variantIds={selectedVariantIds}
              field="activatedUntil"
              disabled={!!getActionDisabledTooltip("phaseOut", tabContext)}
              disabledTooltip={getActionDisabledTooltip("phaseOut", tabContext)}
            />
          )}
        </Commandbar>
      )}
    </>
  )
}
